import {Kbd, Popover, createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {OMNIBOX_ANALYTICS_ACTIONS} from 'constants/analytics-products/OmniboxAnalyticsConstants';
import {useFeedbackForm, LLMFeedbackFormContents} from 'llm/feedback/LLMFeedbackFormContents';
import {isLLMFeedbackEnabled} from 'llm/feedback/LLMFeedbackUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as Keyboard from 'util/Keyboard';

const useStyles = createStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    borderTop: '1px solid #e9ecef',
    backgroundColor: '#f8f9fa',
    flexShrink: 0,
    fontSize: 13,
    color: '#868e96',
  },
  hints: {
    display: 'flex',
    gap: 14,
    alignItems: 'center',
  },
  kbd: {
    fontSize: 13,
  },
  feedback: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  buttonContainer: {
    marginTop: 8,
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
}));

enum FeedbackReaction {
  THUMBS_UP = 'THUMBS_UP',
  THUMBS_DOWN = 'THUMBS_DOWN',
}

const REACTION_DISPLAY: Record<FeedbackReaction, string> = {
  [FeedbackReaction.THUMBS_UP]: '👍',
  [FeedbackReaction.THUMBS_DOWN]: '👎',
};

const REACTION_BUTTON_PROPS: Record<FeedbackReaction, {'aria-label': string; icon: BenchlingIconName}> = {
  [FeedbackReaction.THUMBS_UP]: {'aria-label': 'Thumbs up', icon: 'thumbs-up'},
  [FeedbackReaction.THUMBS_DOWN]: {'aria-label': 'Thumbs down', icon: 'thumbs-down'},
};

const OmniboxFeedbackState = makeUseGlobalClientState<{
  submittedReaction: FeedbackReaction | null;
  submittedTextFeedback: boolean;
}>({
  submittedReaction: null,
  submittedTextFeedback: false,
});

function useFeedbackPopoverToggle(clearForm: () => void): {
  isTextFeedbackOpen: boolean;
  openAndFocusTextFeedback: () => void;
  closeTextFeedback: () => void;
  focusTextArea: () => void;
  textFeedbackRef: React.RefObject<HTMLTextAreaElement>;
} {
  const [openCount, setOpenCount] = useState(0);
  const textFeedbackRef = useRef<HTMLTextAreaElement>(null);

  const openAndFocusTextFeedback = useCallback(() => {
    setOpenCount((x) => x + 1);
  }, []);

  const closeTextFeedback = useCallback(() => {
    setOpenCount(0);
    clearForm();
  }, [clearForm]);

  const focusTextArea = useCallback(() => {
    if (openCount > 0 && textFeedbackRef.current) {
      textFeedbackRef.current.focus();
    }
  }, [openCount]);

  useEffect(() => {
    focusTextArea();
  }, [openCount, focusTextArea]);

  return {
    isTextFeedbackOpen: openCount > 0,
    openAndFocusTextFeedback,
    closeTextFeedback,
    focusTextArea,
    textFeedbackRef,
  };
}

interface OmniboxFooterProps {
  hasNavigableResults: boolean;
  query: string;
  requestId: string | null;
  hasDirectAnswer: boolean;
  searchResultCount: number;
}

export default function OmniboxFooter({
  hasNavigableResults,
  query,
  requestId,
  hasDirectAnswer,
  searchResultCount,
}: OmniboxFooterProps): JSX.Element {
  const {classes} = useStyles();
  const showFeedback =
    hasNavigableResults && query.trim() !== '' && isLLMFeedbackEnabled() && requestId != null;

  const stateKey = requestId ?? '__none__';
  const [state, setState] = OmniboxFeedbackState.useGlobalClientState(stateKey);

  const {
    formProps,
    clearForm,
    submitForm,
    disabled: textFeedbackSubmitDisabled,
  } = useFeedbackForm({
    title: 'Omnibox feedback',
    requiredContextItems: [
      {
        name: 'Reaction',
        value: state.submittedReaction ? REACTION_DISPLAY[state.submittedReaction] : 'Unknown',
      },
      {name: 'Request ID', value: requestId ?? 'Unknown'},
      {name: 'Has Direct Answer', value: String(hasDirectAnswer)},
      {name: 'Search Result Count', value: String(searchResultCount)},
    ],
    optionalContextItems: [],
  });

  const {isTextFeedbackOpen, openAndFocusTextFeedback, closeTextFeedback, focusTextArea, textFeedbackRef} =
    useFeedbackPopoverToggle(clearForm);

  const formRef = useRef<HTMLFormElement>(null);
  const handleFormSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      await submitForm();
      setState({submittedTextFeedback: true, submittedReaction: state.submittedReaction});
      closeTextFeedback();
    },
    [submitForm, state.submittedReaction, closeTextFeedback, setState]
  );

  const handleFormKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
    if (Keyboard.isOSCtrlKey(e) && Keyboard.isEnterKey(e)) {
      e.preventDefault();
      formRef.current!.requestSubmit();
    }
  }, []);

  return (
    <div className={classes.root}>
      {showFeedback && (
        <div className={classes.feedback}>
          {[FeedbackReaction.THUMBS_UP, FeedbackReaction.THUMBS_DOWN].map((reaction) => (
            <Button
              key={reaction}
              {...REACTION_BUTTON_PROPS[reaction]}
              buttonStyle={
                state.submittedReaction === reaction
                  ? ButtonStyle.TERTIARY_BLUE_POWDER
                  : ButtonStyle.TERTIARY_WHITE
              }
              size={ButtonSize.SMALL}
              onClick={() => {
                if (state.submittedReaction === reaction) {
                  return;
                }
                trackEvent(OMNIBOX_ANALYTICS_ACTIONS.SUBMITTED_OMNIBOX_REACTION_FEEDBACK, {
                  reaction,
                  requestId,
                  query,
                });
                setState({submittedTextFeedback: false, submittedReaction: reaction});

                if (reaction === FeedbackReaction.THUMBS_UP) {
                  focusTextArea();
                } else {
                  openAndFocusTextFeedback();
                }
              }}
              dataTestAttrs={{component: 'OmniboxFeedback', element: 'reactionButton', key: reaction}}
            />
          ))}
          {state.submittedReaction != null && (
            <Popover
              opened={isTextFeedbackOpen}
              closeOnClickOutside={false}
              withArrow={true}
              withinPortal={true}
              zIndex={1056}
              arrowSize={20}
              shadow="md"
              width={650}
            >
              <Popover.Target>
                {state.submittedTextFeedback ? (
                  <span className="text-success">
                    <BenchlingIcon icon="check" /> Feedback sent!
                  </span>
                ) : (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openAndFocusTextFeedback();
                    }}
                    {...getTestProps({component: 'OmniboxFeedback', element: 'tellUsMoreLink'})}
                  >
                    Tell us more
                  </a>
                )}
              </Popover.Target>
              <Popover.Dropdown>
                <form ref={formRef} onSubmit={handleFormSubmit} onKeyDown={handleFormKeyDown}>
                  <LLMFeedbackFormContents
                    textAreaRef={textFeedbackRef}
                    isPositiveFeedback={state.submittedReaction === FeedbackReaction.THUMBS_UP}
                    {...formProps}
                  />
                  <div className={classes.buttonContainer}>
                    <Button
                      text="Cancel"
                      buttonStyle={ButtonStyle.TERTIARY_GRAY}
                      size={ButtonSize.MINI}
                      onClick={closeTextFeedback}
                    />
                    <Button
                      text="Submit"
                      type="submit"
                      buttonStyle={ButtonStyle.SECONDARY}
                      size={ButtonSize.MINI}
                      disabled={textFeedbackSubmitDisabled}
                    />
                  </div>
                </form>
              </Popover.Dropdown>
            </Popover>
          )}
        </div>
      )}

      {hasNavigableResults && (
        <div className={classes.hints}>
          <span>
            <Kbd className={classes.kbd}>↑</Kbd> <Kbd className={classes.kbd}>↓</Kbd> navigate
          </span>
          <span>
            <Kbd className={classes.kbd}>↵</Kbd> select
          </span>
          <span>
            <Kbd className={classes.kbd}>esc</Kbd> close
          </span>
          <span>
            <Kbd className={classes.kbd}>%</Kbd> filter
          </span>
        </div>
      )}
    </div>
  );
}
